import { useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { TFormType } from 'src/@types';
import { RHFSelect, RHFSwitch } from 'src/components/hook-form';
import useGetSelectOption from 'src/hooks/useGetSelectOption';
import { FormInnerContainer, FormItem, FormLabel, FormRow } from 'src/layouts/form/FormLayout';
import { ILabelValueModel, useStores } from 'src/models';
import { IPipeline } from 'src/models/management/category/Pipeline';
import { businessApi } from 'src/service';
import { FormType } from 'src/static/constants/common/form';

import { buildCategoryOptions } from '../../../utils/bulidCategoryOptions';

interface CommonProps {
  formType: TFormType;
}
export const Common = observer(({ formType }: CommonProps) => {
  const { t } = useTranslation();
  const isFirstRender = useRef(true);
  const { getUseYn, getLang } = useGetSelectOption();
  const { categoryStore } = useStores();
  const {
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext();

  // 썸네일 파일
  const [file, setFile] = useState<Array<File | string>>([]);
  // 파이프라인 옵션
  const [pipelineOptions, setPipelineOptions] = useState<ILabelValueModel[]>([
    { value: '', label: '없음' },
  ]);

  // 최상위 카테고리 제외하고 2epth 이하 카테고리만 노출 select option
  const CTEGRY_SELECT_OPTIONS = useMemo(
    () => buildCategoryOptions(toJS(categoryStore.categoryTree)),
    [categoryStore.categoryTree],
  );

  // 카테고리에 따른 파이프라인 선택 옵션
  const fetchPipelineOptions = async () => {
    const categorySid = watch('categorySid');
    if (!categorySid) {
      setPipelineOptions([{ value: '', label: '없음' }]);
      setValue('pipelineSid', '');
      return;
    }

    try {
      const response = await businessApi.getCategoryPipeline(categorySid);
      if (response?.success) {
        // 화면에서 데이터 변환 처리
        const options = (response.data as IPipeline[])
          .filter((item) => item.langCd === watch('langCd'))
          .map((item) => ({
            value: item.pipelineSid ?? '',
            label: item.pipelineNm?.trim() ?? '',
          }));

        setPipelineOptions(options.length > 0 ? options : [{ value: '', label: '없음' }]);

        // 현재 선택된 값이 새 옵션에 없으면 초기화
        const currentValue = watch('pipelineSid');
        if (currentValue && !options.find((opt) => opt.value === currentValue)) {
          setValue('pipelineSid', '');
        }
      }
    } catch (error) {
      console.error('파이프라인 조회 실패:', error);
      setPipelineOptions([{ value: '', label: '없음' }]);
      setValue('pipelineSid', '');
    }
  };

  useEffect(() => {
    if (formType === FormType.UPDATE) {
      setFile([getValues('thumbPath')]);
    }
  }, []);

  useEffect(() => {
    fetchPipelineOptions();
  }, [watch('categorySid'), watch('langCd')]);

  return (
    <Box sx={{ display: 'flex', gap: 2 }}>
      <Box sx={{ flex: 7 }}>
        <FormInnerContainer>
          <FormLabel>{t('common.form.section.basicInfo')}</FormLabel>
          <FormRow sx={{ gridTemplateColumns: '1fr' }}>
            <FormItem>
              <RHFSwitch label={t('business.form.useYn')} name="useYn" />
            </FormItem>
          </FormRow>
          <FormRow sx={{ gridTemplateColumns: '1fr' }}>
            <FormItem>
              <RHFSelect
                label={t('business.form.langCd')}
                name="langCd"
                useAll={false}
                options={getLang()}
              />
            </FormItem>
          </FormRow>
          <FormRow sx={{ gridTemplateColumns: '1fr' }}>
            <FormItem size={2}>
              <RHFSelect
                label={t('business.form.categorySid')}
                name="categorySid"
                options={CTEGRY_SELECT_OPTIONS}
                useAll={false}
              />
            </FormItem>
          </FormRow>
          {pipelineOptions.length > 0 && pipelineOptions[0].value !== '' && (
            <FormRow sx={{ gridTemplateColumns: '1fr' }}>
              <FormItem>
                <RHFSelect
                  label={t('business.form.pipelineSid')}
                  name="pipelineSid"
                  options={pipelineOptions}
                  useAll={false}
                />
              </FormItem>
            </FormRow>
          )}
        </FormInnerContainer>
      </Box>
      {/* TODO 현재 홈페이지에는 필요X 추후 다른 페이지에 필요할 수 있어 대기 */}
      {/* <Box sx={{ overflow: 'hidden' }}>
        <FormInnerContainer sx={{ minHeight: '216px', justifyContent: 'center' }}>
          <FormLabel>{t('business.form.thumbPath')}</FormLabel>
          <FormRow sx={{ gridTemplateColumns: '1fr' }}>
            <FormItem>
              <RHFUploadImage
                name="thumbPath"
                maxFiles={1}
                files={file}
                onChange={(newFile: Array<File | string>) => {
                  setFile(newFile);
                  setValue('thumbPath', newFile[0], {
                    shouldValidate: true, // validation 즉시 실행
                    shouldDirty: true, // 값 변경 즉시 반영
                  });
                }}
              />
              {errors.thumbPath?.message && (
                <UploadErrorMessage message={String(errors.thumbPath.message)} />
              )}
            </FormItem>
          </FormRow>
        </FormInnerContainer>
      </Box> */}
    </Box>
  );
});
